// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — the rules. Pure, synchronous, no DOM, no network, no clock.
//
// ── THE HONESTY SPLIT, AND WHY IT IS A TYPE AND NOT A PROMISE ────────────────────────────────────
//
// `PublicState` holds `crateCount: number` and HAS NO `crate` FIELD. The cards themselves live in
// `Secret` (secret.ts), which the bot's module is forbidden by test to import. `viewFor` deep-copies
// a seat's own satchel into a `SeatView`. So a bot that peeks does not compile, rather than being
// asked politely not to — and the same object is what a guest receives over the wire, so one type
// split does two jobs: an honest opponent and an un-snoopable client.
//
// ── TERMINATION IS PROVED, NOT HOPED FOR ─────────────────────────────────────────────────────────
//
// Let  PSI = crateCount + trestleCount + chitsRemaining   (58 at the start of a deal).
//   TAKE   PSI -1        HIRE  PSI -(porters taken)      BARTER  PSI -k (k >= 2)
//   SELL   PSI -(>=1)    BELL  PSI -1 or 0               DUMP    PSI unchanged
// but DUMP strictly decreases the total number of cards held in hand, and when both hands are empty
// TAKE or HIRE or the exhaustion trigger is available. So (PSI, cards-in-hand) is a strictly
// decreasing lexicographic potential on EVERY ply, which caps a deal at 58 + 12 = 70 plies.
//
// The critical detail is that BARTER's given cards go to the BALE and the trestle refills from the
// crate. Returning them to the trestle — the obvious reading — makes BARTER row-neutral, deletes the
// potential, and re-opens an infinite exchange loop between two players with porters. The mechanism
// audit's counter 8 asserts the potential decreases rather than asserting the 80-ply backstop never
// fires, because the backstop not firing is a symptom and the potential is the reason.

import {
  BERTHS,
  BOUNTY,
  DRY_TO_END,
  HIRE_BONUS,
  MIN_SALE,
  PORTER,
  SATCHEL_LIMIT,
  TARE_ALLOWANCE,
  TARIFF,
  WARES,
  bountyTierFor,
  isWare,
  type BountyTier,
  type Card,
  type Ware,
} from './wares';
import { BELL_PERIOD, FIRST_BELL_PLY, type Mode } from './modes';

export type Seat = 0 | 1;
export const other = (s: Seat): Seat => (s === 0 ? 1 : 0);

/** A deal cannot reach this; it exists so a rulebook bug cannot hang a live room. */
export const PLY_CAP = 80;

export type Action =
  | { readonly k: 'take'; readonly berth: number }
  | { readonly k: 'hire' }
  | {
      readonly k: 'barter';
      readonly berths: readonly number[];
      readonly giveWares: readonly Ware[];
      readonly givePorters: number;
    }
  | { readonly k: 'sell'; readonly ware: Ware; readonly n: number }
  | { readonly k: 'dump' };

export type EndReason = 'dry' | 'exhausted' | 'cap' | 'walkover';

export type PublicEvent =
  /**
   * The opening table. Emitted because it is genuinely PUBLIC — the five berths are face up and the
   * crate size is printed — and because without it nothing downstream can reconstruct the table from
   * the stream alone. The mechanism audit needs exactly this and nothing more; note that it carries
   * no satchel and no crate order, so it leaks nothing.
   */
  | { readonly k: 'deal'; readonly trestle: readonly (Card | null)[]; readonly crate: number }
  | { readonly k: 'take'; readonly seat: Seat; readonly ware: Ware; readonly berth: number }
  | { readonly k: 'hire'; readonly seat: Seat; readonly n: number }
  | {
      readonly k: 'barter';
      readonly seat: Seat;
      /** Which berths were emptied. Public, and the only way a replay can be exact when two berths
       *  happen to hold the same ware. */
      readonly berths: readonly number[];
      readonly took: readonly Ware[];
      readonly gaveWares: readonly Ware[];
      readonly gavePorters: number;
    }
  | {
      readonly k: 'sell';
      readonly seat: Seat;
      readonly ware: Ware;
      readonly cards: number;
      readonly chits: readonly number[];
      readonly tier: BountyTier | null;
      readonly bounty: number;
    }
  | { readonly k: 'dump'; readonly seat: Seat; readonly card: Card }
  | { readonly k: 'fill'; readonly cards: readonly Card[] }
  | { readonly k: 'bell'; readonly ware: Ware | null; readonly chit: number }
  | { readonly k: 'end'; readonly reason: EndReason };

export interface PublicState {
  readonly mode: Mode;
  /** Which deal of the match this is (1-based). A match is the best of three. */
  deal: number;
  ply: number;
  toMove: Seat;
  /** Who took the first action of this deal. The OTHER seat holds the tare allowance. */
  readonly starter: Seat;
  trestle: (Card | null)[];
  /** The one face-up card on top of the crate. Public by rule — a small, legible preview. */
  crateTop: Card | null;
  /** Chits still on each ware's column, dearest first. */
  tariffs: Record<Ware, number[]>;
  bounties: number[][];
  /** A COUNT, including the face-up top. There is deliberately no `crate` here. */
  crateCount: number;
  gang: [number, number];
  satchelCount: [number, number];
  /** Chits banked off tariffs. */
  chits: [number, number];
  /** Chits banked off bounties, kept separate so the summary can break a score down. */
  bounty: [number, number];
  /** Brisk: chits the bell has destroyed. Nobody receives them. */
  burned: number;
  over: boolean;
  endReason: EndReason | null;
  log: PublicEvent[];
}

export interface Secret {
  satchels: [Ware[], Ware[]];
  /** Index 0 is the face-up top. Never sent, never logged, never rendered. */
  crate: Card[];
}

export interface FullState {
  readonly pub: PublicState;
  readonly sec: Secret;
}

/** What a player — human or bot — is allowed to hold. Its own satchel, and the public table. */
export interface SeatView {
  readonly pub: PublicState;
  readonly seat: Seat;
  readonly satchel: readonly Ware[];
}

// ── construction ────────────────────────────────────────────────────────────────────────────────

/**
 * The opening deal is normalised so that NEITHER deal luck NOR porter luck can hand a player a
 * turn-0 lead:
 *   - both openings are exactly five WARES (porters drawn during the deal are set aside),
 *   - the opening trestle is always exactly two porters and three wares.
 * The set-aside cards go back into the crate before the top is flipped. Everything is a pure
 * function of the shuffled order handed in, so two peers and the audit agree by construction.
 */
export function deal(mode: Mode, order: readonly Card[], dealNo: number, starter: Seat): FullState {
  const rest: Card[] = [];
  const aside: Card[] = [];
  // Two porters are lifted out of the deck and placed in berths 4-5 before anything is dealt.
  let lifted = 0;
  for (const c of order) {
    if (lifted < 2 && c === PORTER) {
      lifted++;
      continue;
    }
    rest.push(c);
  }
  const satchels: [Ware[], Ware[]] = [[], []];
  let i = 0;
  for (let n = 0; n < 10; n++) {
    const seat = (n % 2) as Seat;
    while (i < rest.length && !isWare(rest[i])) aside.push(rest[i++]);
    const c = rest[i++];
    satchels[seat].push(c as Ware);
  }
  const trestle: (Card | null)[] = [null, null, null, PORTER, PORTER];
  for (let b = 0; b < 3; b++) {
    while (i < rest.length && !isWare(rest[i])) aside.push(rest[i++]);
    trestle[b] = rest[i++];
  }
  // The set-aside porters go back into the crate, interleaved deterministically so the order stays a
  // pure function of the shuffle rather than depending on how many happened to be skipped.
  const tail = rest.slice(i);
  const crate: Card[] = [];
  const step = aside.length > 0 ? Math.max(1, Math.floor(tail.length / (aside.length + 1))) : 0;
  let ai = 0;
  for (let t = 0; t < tail.length; t++) {
    if (step > 0 && ai < aside.length && t > 0 && t % step === 0) crate.push(aside[ai++]);
    crate.push(tail[t]);
  }
  while (ai < aside.length) crate.push(aside[ai++]);

  const tariffs = {} as Record<Ware, number[]>;
  for (const w of WARES) tariffs[w] = [...TARIFF[w]];

  const pub: PublicState = {
    mode,
    deal: dealNo,
    ply: 0,
    toMove: starter,
    starter,
    trestle,
    crateTop: crate[0] ?? null,
    tariffs,
    bounties: BOUNTY.map((s) => [...s]),
    crateCount: crate.length,
    gang: [0, 0],
    satchelCount: [satchels[0].length, satchels[1].length],
    chits: [0, 0],
    bounty: [0, 0],
    burned: 0,
    over: false,
    endReason: null,
    log: [],
  };
  const fs: FullState = { pub, sec: { satchels, crate } };
  pub.log.push({ k: 'deal', trestle: [...trestle], crate: crate.length });
  beginTurn(fs);
  return fs;
}

export function clonePublic(p: PublicState): PublicState {
  const tariffs = {} as Record<Ware, number[]>;
  for (const w of WARES) tariffs[w] = [...p.tariffs[w]];
  return {
    mode: p.mode,
    deal: p.deal,
    ply: p.ply,
    toMove: p.toMove,
    starter: p.starter,
    trestle: [...p.trestle],
    crateTop: p.crateTop,
    tariffs,
    bounties: p.bounties.map((s) => [...s]),
    crateCount: p.crateCount,
    gang: [...p.gang] as [number, number],
    satchelCount: [...p.satchelCount] as [number, number],
    chits: [...p.chits] as [number, number],
    bounty: [...p.bounty] as [number, number],
    burned: p.burned,
    over: p.over,
    endReason: p.endReason,
    log: [...p.log],
  };
}

export function cloneFull(fs: FullState): FullState {
  return {
    pub: clonePublic(fs.pub),
    sec: {
      satchels: [[...fs.sec.satchels[0]], [...fs.sec.satchels[1]]],
      crate: [...fs.sec.crate],
    },
  };
}

// ── reading the table ───────────────────────────────────────────────────────────────────────────

export const dryCount = (p: PublicState): number =>
  WARES.reduce((n, w) => n + (p.tariffs[w].length === 0 ? 1 : 0), 0);

export const trestleWares = (p: PublicState): Ware[] =>
  p.trestle.filter((c): c is Ware => c !== null && isWare(c));

export const trestlePorters = (p: PublicState): number =>
  p.trestle.reduce((n, c) => n + (c === PORTER ? 1 : 0), 0);

export const trestleCount = (p: PublicState): number =>
  p.trestle.reduce((n, c) => n + (c !== null ? 1 : 0), 0);

export const chitsLeft = (p: PublicState): number =>
  WARES.reduce((n, w) => n + p.tariffs[w].length, 0);

/** The termination potential. Asserted to strictly decrease by the mechanism audit. */
export const potential = (p: PublicState): number => p.crateCount + trestleCount(p) + chitsLeft(p);

/** What a sale of `n` of `w` would pay right now, chit by chit, top first. */
export function quote(p: PublicState, w: Ware, n: number): number[] {
  return p.tariffs[w].slice(0, Math.max(0, n));
}

export const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);

/** The chit the next bell will destroy, or null if nothing is burnable. Rendered as a warning. */
export function doomedChit(p: PublicState): { ware: Ware; chit: number } | null {
  if (!p.mode.bell) return null;
  let best: { ware: Ware; chit: number } | null = null;
  for (const w of WARES) {
    // Never the last chit on a column: a bell that could empty a tariff could end a round, and an
    // exogenous clock that ends rounds is a different, much worse game.
    if (p.tariffs[w].length < 2) continue;
    const top = p.tariffs[w][0];
    if (!best || top > best.chit) best = { ware: w, chit: top };
  }
  return best;
}

export function countOf(satchel: readonly Ware[], w: Ware): number {
  let n = 0;
  for (const c of satchel) if (c === w) n++;
  return n;
}

// ── legality ────────────────────────────────────────────────────────────────────────────────────

export function legal(p: PublicState, seat: Seat, satchel: readonly Ware[], a: Action): boolean {
  if (p.over || p.toMove !== seat) return false;
  const gang = p.gang[seat];
  switch (a.k) {
    case 'take': {
      if (a.berth < 0 || a.berth >= BERTHS) return false;
      const c = p.trestle[a.berth];
      if (c === null || !isWare(c)) return false;
      return satchel.length < SATCHEL_LIMIT;
    }
    case 'hire':
      return trestlePorters(p) > 0;
    case 'barter': {
      const k = a.berths.length;
      if (k < 2) return false;
      if (a.givePorters < 0 || a.giveWares.length + a.givePorters !== k) return false;
      if (a.givePorters > gang) return false;
      if (new Set(a.berths).size !== k) return false;
      for (const b of a.berths) {
        if (b < 0 || b >= BERTHS) return false;
        const c = p.trestle[b];
        if (c === null || !isWare(c)) return false;
      }
      // The given wares must actually be in the satchel, as a MULTISET.
      const held = new Map<Ware, number>();
      for (const w of satchel) held.set(w, (held.get(w) ?? 0) + 1);
      for (const w of a.giveWares) {
        const n = held.get(w) ?? 0;
        if (n === 0) return false;
        held.set(w, n - 1);
      }
      // Satchel after = size - givenWares + taken = size + givenPorters.
      return satchel.length + a.givePorters <= SATCHEL_LIMIT;
    }
    case 'sell': {
      if (a.n < MIN_SALE(a.ware)) return false;
      if (countOf(satchel, a.ware) < a.n) return false;
      return p.tariffs[a.ware].length > 0;
    }
    case 'dump':
      // The liveness valve, and it is legal ONLY when the player is genuinely stuck. Auditing that
      // it never fires alongside another legal action is counter 9.
      return satchel.length + p.gang[seat] > 0 && !hasRealMove(p, seat, satchel);
  }
}

/** Whether any of the four real verbs is available. Used only by DUMP's legality and the audit. */
export function hasRealMove(p: PublicState, seat: Seat, satchel: readonly Ware[]): boolean {
  if (satchel.length < SATCHEL_LIMIT && trestleWares(p).length > 0) return true;
  if (trestlePorters(p) > 0) return true;
  for (const w of WARES) {
    if (p.tariffs[w].length > 0 && countOf(satchel, w) >= MIN_SALE(w)) return true;
  }
  // BARTER: k wares on the trestle, k cards to give, satchel fits afterwards.
  const wares = trestleWares(p).length;
  const holdings = satchel.length + p.gang[seat];
  for (let k = 2; k <= Math.min(wares, holdings); k++) {
    // Give as many wares as we can before touching porters — that is the variant that most easily
    // satisfies the satchel bound (each porter given makes the satchel one bigger).
    const porters = Math.max(0, k - satchel.length);
    if (porters > p.gang[seat]) continue;
    if (satchel.length + porters <= SATCHEL_LIMIT) return true;
  }
  return false;
}

/** Every legal action for a seat, deduplicated. The move generator the bot and the sim both use. */
export function legalActions(p: PublicState, seat: Seat, satchel: readonly Ware[]): Action[] {
  const out: Action[] = [];
  if (p.over || p.toMove !== seat) return out;

  if (satchel.length < SATCHEL_LIMIT) {
    const seen = new Set<Ware>();
    for (let b = 0; b < BERTHS; b++) {
      const c = p.trestle[b];
      if (c === null || !isWare(c)) continue;
      // Two berths holding the same ware are the same move; searching both doubles the tree for
      // nothing.
      if (seen.has(c)) continue;
      seen.add(c);
      out.push({ k: 'take', berth: b });
    }
  }
  if (trestlePorters(p) > 0) out.push({ k: 'hire' });

  for (const w of WARES) {
    const have = countOf(satchel, w);
    const live = p.tariffs[w].length;
    if (live === 0) continue;
    for (let n = MIN_SALE(w); n <= have; n++) out.push({ k: 'sell', ware: w, n });
  }

  for (const a of barterMoves(p, seat, satchel)) out.push(a);

  if (out.length === 0 && satchel.length + p.gang[seat] > 0) out.push({ k: 'dump' });
  return out;
}

/**
 * BARTER is the only combinatorial move in the game, so it is CANONICALISED rather than enumerated:
 * for each k, take the k distinct dearest trestle wares (by live tariff top), and offer three
 * give-sets — all porters, all satchel, and porters-first-then-satchel. That keeps root branching
 * near 40 instead of into the thousands, and every generated move is legal by construction.
 */
export function barterMoves(p: PublicState, seat: Seat, satchel: readonly Ware[]): Action[] {
  const out: Action[] = [];
  const berthsByValue: number[] = [];
  for (let b = 0; b < BERTHS; b++) {
    const c = p.trestle[b];
    if (c !== null && isWare(c)) berthsByValue.push(b);
  }
  berthsByValue.sort((x, y) => {
    const cx = p.trestle[x] as Ware;
    const cy = p.trestle[y] as Ware;
    return (p.tariffs[cy][0] ?? 0) - (p.tariffs[cx][0] ?? 0);
  });
  const gang = p.gang[seat];
  // Cheapest satchel cards first — the ones whose column is nearly dry are the ones worth spending.
  const spendable = [...satchel].sort(
    (a, b) => (p.tariffs[a][0] ?? 0) - (p.tariffs[b][0] ?? 0),
  );
  const maxK = Math.min(berthsByValue.length, satchel.length + gang, 4);
  for (let k = 2; k <= maxK; k++) {
    const berths = berthsByValue.slice(0, k);
    const variants: { porters: number }[] = [
      { porters: Math.min(k, gang) },
      { porters: 0 },
      { porters: Math.max(0, k - satchel.length) },
    ];
    const seen = new Set<number>();
    for (const v of variants) {
      const porters = v.porters;
      if (porters < 0 || porters > gang || seen.has(porters)) continue;
      const wares = k - porters;
      if (wares > satchel.length) continue;
      if (satchel.length + porters > SATCHEL_LIMIT) continue;
      seen.add(porters);
      out.push({ k: 'barter', berths, giveWares: spendable.slice(0, wares), givePorters: porters });
    }
  }
  return out;
}

// ── applying an action ──────────────────────────────────────────────────────────────────────────

/**
 * Apply `a` for the seat to move. Returns false and leaves the state untouched if it is illegal —
 * which is what makes the host's validation of a guest's proposal a one-liner.
 */
export function step(fs: FullState, a: Action): boolean {
  const p = fs.pub;
  const seat = p.toMove;
  const satchel = fs.sec.satchels[seat];
  if (!legal(p, seat, satchel, a)) return false;

  switch (a.k) {
    case 'take': {
      const w = p.trestle[a.berth] as Ware;
      p.trestle[a.berth] = null;
      satchel.push(w);
      p.log.push({ k: 'take', seat, ware: w, berth: a.berth });
      break;
    }
    case 'hire': {
      let n = 0;
      for (let b = 0; b < BERTHS; b++) {
        if (p.trestle[b] === PORTER) {
          p.trestle[b] = null;
          n++;
        }
      }
      p.gang[seat] += n;
      p.log.push({ k: 'hire', seat, n });
      break;
    }
    case 'barter': {
      const took: Ware[] = [];
      for (const b of a.berths) {
        took.push(p.trestle[b] as Ware);
        p.trestle[b] = null;
      }
      for (const w of a.giveWares) satchel.splice(satchel.indexOf(w), 1);
      p.gang[seat] -= a.givePorters;
      for (const w of took) satchel.push(w);
      // The given cards go to the BALE — out of the game. See the termination note at the top.
      p.log.push({
        k: 'barter',
        seat,
        berths: [...a.berths],
        took,
        gaveWares: [...a.giveWares],
        gavePorters: a.givePorters,
      });
      break;
    }
    case 'sell': {
      const paid = p.tariffs[a.ware].splice(0, Math.min(a.n, p.tariffs[a.ware].length));
      for (let i = 0; i < a.n; i++) satchel.splice(satchel.indexOf(a.ware), 1);
      const total = sum(paid);
      p.chits[seat] += total;
      const tier = bountyTierFor(total, a.n);
      let got = 0;
      if (tier !== null && p.bounties[tier].length > 0) {
        got = p.bounties[tier].shift() as number;
        p.bounty[seat] += got;
      }
      p.log.push({
        k: 'sell',
        seat,
        ware: a.ware,
        cards: a.n,
        chits: paid,
        tier: got > 0 ? tier : null,
        bounty: got,
      });
      break;
    }
    case 'dump': {
      let card: Card;
      if (satchel.length > 0) card = satchel.pop() as Ware;
      else {
        p.gang[seat]--;
        card = PORTER;
      }
      p.log.push({ k: 'dump', seat, card });
      break;
    }
  }

  p.satchelCount = [fs.sec.satchels[0].length, fs.sec.satchels[1].length];

  // Refill: after TAKE / HIRE / BARTER only, and only in a mode that refills continuously.
  if (p.mode.refill === 'always' && (a.k === 'take' || a.k === 'hire' || a.k === 'barter')) {
    refill(fs);
  }

  p.ply++;

  if (dryCount(p) >= DRY_TO_END) {
    end(fs, 'dry');
    return true;
  }

  p.toMove = other(seat);

  if (p.mode.bell && p.ply % BELL_PERIOD === 0 && p.ply >= FIRST_BELL_PLY) ring(fs);

  if (p.ply >= PLY_CAP) {
    end(fs, 'cap');
    return true;
  }

  beginTurn(fs);
  return true;
}

/** Fill empty berths from the crate, in berth order, and flip the new top. */
function refill(fs: FullState): void {
  const p = fs.pub;
  const placed: Card[] = [];
  for (let b = 0; b < BERTHS; b++) {
    if (p.trestle[b] !== null) continue;
    if (fs.sec.crate.length === 0) break;
    const c = fs.sec.crate.shift() as Card;
    p.trestle[b] = c;
    placed.push(c);
  }
  p.crateCount = fs.sec.crate.length;
  p.crateTop = fs.sec.crate[0] ?? null;
  if (placed.length > 0) p.log.push({ k: 'fill', cards: placed });
}

/**
 * BRISK's bell. It burns the single dearest chit on the table, and NOBODY receives it. Three
 * properties make it a mode rather than a tax: it never takes from a column holding one chit (so it
 * can never empty a tariff and can never end a deal), it is announced a full turn-pair in advance
 * (so it is a recurring "go now" moment rather than a random loss), and ringing with nothing
 * burnable is a legal, expected state that the audit permits exactly.
 */
function ring(fs: FullState): void {
  const p = fs.pub;
  const doomed = doomedChit(p);
  if (!doomed) {
    p.log.push({ k: 'bell', ware: null, chit: 0 });
    return;
  }
  p.tariffs[doomed.ware].shift();
  p.burned += doomed.chit;
  p.log.push({ k: 'bell', ware: doomed.ware, chit: doomed.chit });
}

/**
 * The start of a turn. Dearth's refill lives here — it is the ONLY place that mode's trestle is
 * replenished — and so does the second end-of-deal trigger, which is uniform across all modes:
 * the crate is empty and there is nothing left to buy.
 */
export function beginTurn(fs: FullState): void {
  const p = fs.pub;
  if (p.over) return;
  if (p.mode.refill === 'onEmpty' && trestleCount(p) === 0) refill(fs);
  if (p.crateCount === 0 && trestleWares(p).length === 0) end(fs, 'exhausted');
}

export function end(fs: FullState, reason: EndReason): void {
  const p = fs.pub;
  if (p.over) return;
  p.over = true;
  p.endReason = reason;
  p.log.push({ k: 'end', reason });
}

// ── scoring ─────────────────────────────────────────────────────────────────────────────────────

export interface Score {
  readonly chits: number;
  readonly bounty: number;
  readonly hire: number;
  readonly tare: number;
  readonly total: number;
}

export function scoreOf(p: PublicState, seat: Seat): Score {
  const chits = p.chits[seat];
  const bounty = p.bounty[seat];
  const hire = p.gang[seat] > p.gang[other(seat)] ? HIRE_BONUS : 0;
  const tare = seat === p.starter ? 0 : TARE_ALLOWANCE;
  return { chits, bounty, hire, tare, total: chits + bounty + hire + tare };
}

/**
 * Who won the deal. Highest total, then the larger gang, then the seat that did NOT start it. There
 * are no draws and no replays: a tie broken by a coin flip is a tie the loser can blame on luck, and
 * a replay is a deadlock waiting for the one player who has walked away.
 */
export function dealWinner(p: PublicState): Seat {
  const a = scoreOf(p, 0).total;
  const b = scoreOf(p, 1).total;
  if (a !== b) return a > b ? 0 : 1;
  if (p.gang[0] !== p.gang[1]) return p.gang[0] > p.gang[1] ? 0 : 1;
  return other(p.starter);
}
